/* eslint-disable */
import React from 'react'
import { api, canonicalStoredFileIdForApiPath } from '../lib/api'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import LeadPrivacyConsent from './categoryLanding/LeadPrivacyConsent'
import { normalizeLandingPageData, buildNavigationFromSections } from '../features/orgHomepage/normalize'

function getImageUrl(fileId) {
  if (!fileId) return null
  const raw = String(fileId)
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/')) {
    return raw
  }
  const apiBase = (api?.defaults?.baseURL || '').replace(/\/$/, '')
  return `${apiBase}/files/${encodeURIComponent(canonicalStoredFileIdForApiPath(raw))}/preview`
}

function resolveAssetFileId(asset) {
  if (!asset || typeof asset !== 'object') return ''
  return asset.fileId || ''
}

function getYoutubeEmbedUrl(rawUrl) {
  const source = String(rawUrl || '').trim()
  if (!source) return ''
  try {
    const url = source.startsWith('http://') || source.startsWith('https://')
      ? new URL(source)
      : new URL(`https://${source}`)
    const host = url.hostname.replace(/^www\./, '').toLowerCase()
    let videoId = ''

    if (host === 'youtu.be') {
      videoId = url.pathname.split('/').filter(Boolean)[0] || ''
    } else if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
      if (url.pathname === '/watch') {
        videoId = url.searchParams.get('v') || ''
      } else if (url.pathname.startsWith('/embed/')) {
        videoId = url.pathname.split('/')[2] || ''
      } else if (url.pathname.startsWith('/shorts/')) {
        videoId = url.pathname.split('/')[2] || ''
      } else if (url.pathname.startsWith('/live/')) {
        videoId = url.pathname.split('/')[2] || ''
      }
    }

    return videoId ? `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?rel=0&modestbranding=1` : ''
  } catch {
    return ''
  }
}

function useInView({ threshold = 0.2, once = true, rootMargin = '0px 0px -10% 0px' } = {}) {
  const ref = React.useRef(null)
  const [visible, setVisible] = React.useState(false)

  React.useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return undefined
    }
    const root = node.closest('.preview-container') || null
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          if (once) observer.unobserve(entry.target)
        } else if (!once) {
          setVisible(false)
        }
      },
      { threshold, root, rootMargin }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [once, rootMargin, threshold])

  return [ref, visible]
}

function formatAnimatedValue(amount, decimals) {
  return new Intl.NumberFormat('ko-KR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount)
}

function CountUpValue({ value, active }) {
  const raw = String(value || '')
  const parsed = React.useMemo(() => {
    const match = raw.match(/^([^0-9-]*)(-?\d[\d,]*(?:\.\d+)?)(.*)$/)
    if (!match) return null
    return {
      prefix: match[1],
      numericText: match[2],
      suffix: match[3],
      decimals: (match[2].split('.')[1] || '').length,
      target: Number(match[2].replace(/,/g, '')),
    }
  }, [raw])
  const [display, setDisplay] = React.useState(() => (parsed ? `${parsed.prefix}0${parsed.suffix}` : raw))

  React.useEffect(() => {
    if (!parsed) {
      setDisplay(raw)
      return undefined
    }
    if (!active) {
      setDisplay(`${parsed.prefix}0${parsed.suffix}`)
      return undefined
    }
    if (Number.isNaN(parsed.target)) {
      setDisplay(raw)
      return undefined
    }
    const duration = 1400
    const start = performance.now()
    let raf = 0

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const current = parsed.target * eased
      setDisplay(`${parsed.prefix}${formatAnimatedValue(current, parsed.decimals)}${parsed.suffix}`)
      if (progress < 1) {
        raf = window.requestAnimationFrame(tick)
      } else {
        setDisplay(raw)
      }
    }

    raf = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(raf)
  }, [active, parsed, raw])

  return display
}

function AnimatedSection({ id, sectionRef, children }) {
  const [ref, visible] = useInView({ threshold: 0.15 })
  return (
    <div
      ref={(node) => {
        ref.current = node
        if (typeof sectionRef === 'function') sectionRef(node)
      }}
      id={id}
      className={`scroll-mt-24 transform-gpu transition-all duration-1000 ease-out ${visible ? 'translate-y-0 scale-100 opacity-100 blur-0' : 'translate-y-10 scale-[0.985] opacity-0 blur-[2px]'}`}
    >
      {children(visible)}
    </div>
  )
}

function SectionVideoEmbed({ url, title, isVisible, delayMs = 0, className = '', badge = 'YouTube' }) {
  const embedUrl = getYoutubeEmbedUrl(url)
  if (!embedUrl) return null
  return (
    <div
      className={`overflow-hidden rounded-[28px] border border-slate-200/80 bg-white/90 p-2 shadow-[0_20px_60px_rgba(15,23,42,0.14)] backdrop-blur-sm transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'} ${className}`}
      style={{ transitionDelay: `${delayMs}ms` }}
    >
      <div className="mb-2 flex items-center justify-between px-1.5 pt-1">
        <span className="inline-flex rounded-full bg-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/90">
          {badge}
        </span>
      </div>
      <div className="aspect-video overflow-hidden rounded-[22px] bg-slate-950">
        <iframe
          src={embedUrl}
          title={title || 'YouTube video'}
          className="h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    </div>
  )
}

function HeroSection({ section, primaryColor, onNavigate, isVisible }) {
  const content = section.content || {}
  const bg = resolveAssetFileId(section.assets?.backgroundImage)
  return (
    <section
      className="relative min-h-[88vh] overflow-hidden pt-20 text-white md:min-h-screen md:pt-24"
      style={{
        backgroundColor: primaryColor,
      }}
    >
      {bg ? (
        <div
          className="absolute inset-0 org-hero-zoom"
          style={{
            backgroundImage: `linear-gradient(rgba(15,23,42,0.55), rgba(15,23,42,0.65)), url(${getImageUrl(bg)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      ) : null}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.18),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(125,211,252,0.22),transparent_28%)]" />
      <div className="org-float absolute left-[8%] top-[22%] h-32 w-32 rounded-full bg-white/8 blur-3xl" />
      <div className="org-float absolute bottom-[14%] right-[10%] h-44 w-44 rounded-full bg-sky-300/20 blur-3xl" style={{ animationDelay: '1.2s' }} />
      <div className="mx-auto flex min-h-[calc(88vh-80px)] max-w-6xl items-center px-4 py-12 md:min-h-[calc(100vh-96px)] md:px-6 md:py-16">
        <div className={`relative z-10 w-full ${section.templateCode === 'hero.left-v1' ? 'max-w-3xl text-left' : 'mx-auto max-w-4xl text-center'}`}>
          <p className={`mb-4 whitespace-pre-line text-sm uppercase tracking-[0.35em] text-white/75 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>{content.eyebrow || 'Recruiting Landing'}</p>
          <h1 className={`whitespace-pre-line text-4xl font-black leading-tight md:text-6xl transition-all duration-1000 delay-100 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}>{content.title || ''}</h1>
          {content.subtitle ? (
            <p className={`mt-6 whitespace-pre-line text-lg leading-8 text-white/85 md:text-2xl transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}>{content.subtitle}</p>
          ) : null}
          {content.ctaText ? (
            <div className={`${section.templateCode === 'hero.left-v1' ? 'justify-start' : 'justify-center'} mt-10 flex`}>
              <button
                type="button"
                onClick={() => {
                  if (String(content.ctaLink || '').startsWith('#')) {
                    onNavigate(String(content.ctaLink || '').replace('#', ''))
                    return
                  }
                  if (content.ctaLink) {
                    window.open(content.ctaLink, '_blank', 'noopener,noreferrer')
                  }
                }}
                className={`w-full rounded-full bg-white px-8 py-4 text-base font-semibold text-slate-900 shadow-lg transition hover:-translate-y-1 hover:scale-[1.02] hover:bg-slate-100 md:w-auto ${isVisible ? 'translate-y-0 opacity-100 delay-300' : 'translate-y-6 opacity-0'} duration-1000`}
              >
                {content.ctaText}
              </button>
            </div>
          ) : null}
          <SectionVideoEmbed
            url={content.youtubeUrl}
            title={content.title || 'hero-video'}
            isVisible={isVisible}
            delayMs={380}
            className="mx-auto mt-10 max-w-4xl bg-white/10 ring-1 ring-white/10"
          />
        </div>
      </div>
    </section>
  )
}

function IntroSection({ section, primaryColor, isVisible }) {
  const content = section.content || {}
  const image = resolveAssetFileId(section.assets?.mainImage)
  const hasYoutube = Boolean(getYoutubeEmbedUrl(content.youtubeUrl))
  const cardMode = String(section.templateCode || '').endsWith('card-v1') || String(section.templateCode || '').endsWith('cards-v1')
  const mediaBlock = hasYoutube ? (
    <SectionVideoEmbed
      url={content.youtubeUrl}
      title={content.title || section.type}
      isVisible={isVisible}
      delayMs={180}
      badge="Section Video"
      className="h-full"
    />
  ) : image ? (
    <img src={getImageUrl(image)} alt={content.title || section.type} className="h-full w-full rounded-[32px] object-cover shadow-xl transition duration-700 hover:scale-[1.02]" />
  ) : null
  return (
    <section className="px-6 py-14 md:py-16">
      <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-2 md:items-center">
        <div className={`${section.type === 'ceo-message' && section.templateCode === 'ceo-message.centered-v1' ? 'md:col-span-2 text-center' : ''} transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className={`inline-flex max-w-full rounded-full bg-slate-100 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 transition-all duration-700 whitespace-pre-line ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            {content.eyebrow || (
              section.type === 'ga-intro' ? 'GA Intro'
                : section.type === 'ceo-message' ? 'Leader Message'
                  : 'Business Intro'
            )}
          </div>
          {content.title ? <h2 className={`mt-5 whitespace-pre-line text-3xl font-black text-slate-900 transition-all duration-1000 delay-75 md:text-5xl ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}>{content.title}</h2> : null}
          {content.subtitle ? <p className={`mt-4 whitespace-pre-line text-lg font-semibold text-slate-600 transition-all duration-1000 delay-150 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}>{content.subtitle}</p> : null}
          {content.body ? (
            <div className={`mt-6 whitespace-pre-line leading-8 text-slate-700 transition-all duration-1000 delay-200 ${cardMode ? 'rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100' : ''} ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
              {content.body}
            </div>
          ) : null}
        </div>
        {mediaBlock && section.templateCode !== 'ceo-message.centered-v1' ? (
          <div className={`transition-all duration-1000 delay-150 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            {mediaBlock}
          </div>
        ) : null}
      </div>
      {mediaBlock && section.templateCode === 'ceo-message.centered-v1' ? (
        <div className={`mx-auto mt-10 max-w-sm transition-all duration-1000 delay-150 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          {hasYoutube ? (
            mediaBlock
          ) : (
            <img src={getImageUrl(image)} alt={content.title || section.type} className="aspect-square w-full rounded-full object-cover shadow-xl ring-8 ring-white transition duration-700 hover:scale-[1.02]" />
          )}
        </div>
      ) : null}
      {section.type === 'business-intro' && Array.isArray(content.items) && content.items.length > 0 ? (
        <div className="mx-auto mt-10 grid max-w-6xl gap-4 md:grid-cols-3">
          {content.items.map((item, idx) => (
            <div
              key={`${section.id}-business-item-${idx}`}
              className={`rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-700 hover:-translate-y-2 hover:shadow-xl ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
              style={{ transitionDelay: `${idx * 100 + 180}ms` }}
            >
              <h3 className="text-lg font-bold text-slate-900">{item.title || `항목 ${idx + 1}`}</h3>
              <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-600">{item.body || ''}</p>
            </div>
          ))}
        </div>
      ) : null}
      <div className={`mx-auto mt-8 h-px max-w-6xl bg-slate-200 transition-all duration-1000 delay-300 ${isVisible ? 'scale-x-100 opacity-100' : 'scale-x-90 opacity-0'}`} style={{ backgroundColor: `${primaryColor}22` }} />
    </section>
  )
}

function StatsSection({ section, primaryColor, isVisible }) {
  const items = Array.isArray(section.content?.items) ? section.content.items : []
  if (items.length === 0) return null
  return (
    <section className="relative overflow-hidden bg-slate-950 px-4 py-12 text-white md:px-6 md:py-16">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(96,165,250,0.12),transparent_28%)]" />
      <div className="org-float absolute left-[10%] top-[18%] h-24 w-24 rounded-full bg-white/5 blur-3xl" />
      <div className="org-float absolute bottom-[12%] right-[9%] h-36 w-36 rounded-full bg-sky-300/10 blur-3xl" style={{ animationDelay: '1s' }} />
      <div className="relative mx-auto max-w-6xl">
        {section.content?.title ? <h2 className={`mb-10 text-center text-3xl font-black whitespace-pre-line md:text-5xl transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>{section.content.title}</h2> : null}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
          {items.map((item, idx) => (
            <div
              key={`${section.id}-stat-${idx}`}
              className={`group rounded-3xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur-sm transition-all duration-700 hover:-translate-y-2 hover:scale-[1.015] hover:bg-white/10 hover:shadow-[0_24px_60px_rgba(15,23,42,0.28)] ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
              style={{ transitionDelay: `${idx * 90 + 120}ms` }}
            >
              <div className="text-sm font-medium text-white/65">{item.label || ''}</div>
              <div className="mt-3 text-2xl font-black text-white drop-shadow-[0_0_24px_rgba(255,255,255,0.18)] transition-transform duration-700 group-hover:scale-[1.03] md:text-5xl">
                <CountUpValue value={item.value || '-'} active={isVisible} />
              </div>
            </div>
          ))}
        </div>
        <SectionVideoEmbed
          url={section.content?.youtubeUrl}
          title={section.content?.title || 'stats-video'}
          isVisible={isVisible}
          delayMs={260}
          badge="Insights Video"
          className="mx-auto mt-10 max-w-5xl border-white/10 bg-white/5"
        />
      </div>
    </section>
  )
}

function BenefitsSection({ section, isVisible }) {
  const items = Array.isArray(section.content?.items) ? section.content.items : []
  return (
    <section className="px-6 py-14">
      <div className="mx-auto max-w-6xl">
        {section.content?.title ? <h2 className={`text-center text-3xl font-black whitespace-pre-line text-slate-900 md:text-5xl transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>{section.content.title}</h2> : null}
        {section.content?.subtitle ? <p className={`mx-auto mt-4 max-w-3xl text-center text-lg leading-8 whitespace-pre-line text-slate-600 transition-all duration-1000 delay-100 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>{section.content.subtitle}</p> : null}
        <div className={`mt-10 grid gap-5 ${section.templateCode === 'benefits.list-v1' ? 'md:grid-cols-2' : 'md:grid-cols-4'}`}>
          {items.map((item, idx) => (
            <div
              key={`${section.id}-benefit-${idx}`}
              className={`rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-700 hover:-translate-y-2 hover:scale-[1.01] hover:shadow-xl ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
              style={{ transitionDelay: `${idx * 110 + 180}ms` }}
            >
              <div className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">{String(idx + 1).padStart(2, '0')}</div>
              <h3 className="mt-3 text-xl font-bold text-slate-900">{item.title || `혜택 ${idx + 1}`}</h3>
              <p className="mt-3 whitespace-pre-line text-sm leading-7 text-slate-600">{item.body || ''}</p>
            </div>
          ))}
        </div>
        <SectionVideoEmbed
          url={section.content?.youtubeUrl}
          title={section.content?.title || 'benefits-video'}
          isVisible={isVisible}
          delayMs={260}
          badge="Benefits Video"
          className="mx-auto mt-10 max-w-5xl"
        />
      </div>
    </section>
  )
}

function ContactSection({ section, primaryColor, isVisible }) {
  const content = section.content || {}
  return (
    <section className="bg-slate-50 px-6 py-14">
      <div className={`mx-auto max-w-3xl rounded-[32px] bg-white p-8 text-center shadow-sm ring-1 ring-slate-100 transition-all duration-1000 md:p-12 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        {content.title ? <h2 className="whitespace-pre-line text-3xl font-black text-slate-900 md:text-5xl">{content.title}</h2> : null}
        <div className="mt-8 space-y-4 text-base leading-7 text-slate-700 md:text-lg">
          {content.address ? <p><strong>주소</strong> {content.address}</p> : null}
          {content.phone ? <p><strong>전화</strong> <a href={`tel:${content.phone}`} style={{ color: primaryColor }}>{content.phone}</a></p> : null}
          {content.email ? <p><strong>이메일</strong> <a href={`mailto:${content.email}`} style={{ color: primaryColor }}>{content.email}</a></p> : null}
        </div>
        {content.kakaoLink ? (
          <div className="mt-8">
            <a
              href={content.kakaoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex rounded-full px-6 py-3 text-sm font-semibold text-slate-900"
              style={{ backgroundColor: '#fde047' }}
            >
              카카오톡 문의하기
            </a>
          </div>
        ) : null}
        <SectionVideoEmbed
          url={content.youtubeUrl}
          title={content.title || 'contact-video'}
          isVisible={isVisible}
          delayMs={200}
          badge="Contact Video"
          className="mx-auto mt-8 max-w-4xl"
        />
      </div>
    </section>
  )
}

function LeadFormSection({
  section,
  orgId,
  isPreview,
  primaryColor,
  consentPayload,
  isVisible,
}) {
  const fields = Array.isArray(section.content?.fields) ? section.content.fields : []
  const [form, setForm] = React.useState({})
  const [agreed, setAgreed] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [success, setSuccess] = React.useState('')
  const [error, setError] = React.useState('')

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

  const onSubmit = async (e) => {
    e.preventDefault()
    if (isPreview || !orgId) return
    setSubmitting(true)
    setError('')
    try {
      const responses = {}
      fields.forEach((field) => {
        if (!['customerName', 'customerPhone', 'preferredContactTime', 'message'].includes(field.key)) {
          responses[field.key] = form[field.key] || ''
        }
      })
      await api.post(`/orgs/${orgId}/landing-page/public/leads`, {
        customerName: form.customerName || '',
        customerPhone: form.customerPhone || '',
        preferredContactTime: form.preferredContactTime || '',
        message: form.message || '',
        responses,
        consentAccepted: agreed,
        consentVersion: consentPayload?.collectUse?.version || '',
        thirdPartyConsentAccepted: agreed,
        thirdPartyConsentVersion: consentPayload?.thirdParty?.version || '',
        website: '',
      })
      setSuccess(section.content?.successMessage || '입사문의가 접수되었습니다.')
      setForm({})
      setAgreed(false)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || '입사문의 접수 중 오류가 발생했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="bg-slate-900 px-4 py-12 text-white md:px-6 md:py-16">
      <div className={`mx-auto grid max-w-6xl gap-10 ${resolveAssetFileId(section.assets?.sideImage) ? 'md:grid-cols-[1.1fr_0.9fr]' : ''}`}>
        <div className={`transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          {section.content?.title ? <h2 className="whitespace-pre-line text-3xl font-black leading-tight md:text-5xl">{section.content.title}</h2> : null}
          {section.content?.subtitle ? <p className="mt-5 whitespace-pre-line text-lg leading-8 text-white/75">{section.content.subtitle}</p> : null}
          {success ? <div className="mt-6 rounded-2xl bg-emerald-500/15 px-4 py-3 text-sm text-emerald-200">{success}</div> : null}
          {error ? <div className="mt-6 rounded-2xl bg-red-500/15 px-4 py-3 text-sm text-red-200">{error}</div> : null}
          <form className="mt-8 space-y-4" onSubmit={onSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
            {fields.map((field, idx) => {
              const isSelect = field.type === 'select'
              const commonProps = {
                className: `w-full rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-white/40 focus:outline-none ${field.type === 'textarea' ? 'md:col-span-2' : ''} ${isSelect ? 'appearance-none pr-10' : ''}`,
                placeholder: field.label,
                value: form[field.key] || '',
                onChange: (e) => setField(field.key, e.target.value),
                disabled: isPreview || submitting,
                required: Boolean(field.required),
              }
              if (field.type === 'textarea') {
                return <textarea key={`${field.key}-${idx}`} rows={4} {...commonProps} />
              }
              if (field.type === 'select') {
                return (
                  <div key={`${field.key}-${idx}`} className="relative">
                    <select {...commonProps}>
                      <option value="" style={{ color: '#0f172a', backgroundColor: '#ffffff' }}>{field.label}</option>
                      {(field.options || []).map((option) => (
                        <option
                          key={`${field.key}-${option}`}
                          value={option}
                          style={{ color: '#0f172a', backgroundColor: '#ffffff' }}
                        >
                          {option}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-white/70">▾</span>
                  </div>
                )
              }
              return <input key={`${field.key}-${idx}`} type={field.type || 'text'} {...commonProps} />
            })}
            </div>
            <LeadPrivacyConsent
              collectUse={consentPayload?.collectUse}
              thirdParty={consentPayload?.thirdParty}
              checked={agreed}
              onChange={setAgreed}
              accentColor={primaryColor}
              theme="dark"
              className="pt-2"
            />
            <button
              type="submit"
              disabled={isPreview || submitting || !agreed || (!consentPayload && !isPreview)}
              className="inline-flex w-full justify-center rounded-full px-6 py-3 text-sm font-semibold text-slate-950 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
              style={{ backgroundColor: '#ffffff' }}
            >
              {submitting ? '제출 중...' : section.content?.submitText || '입사문의하기'}
            </button>
          </form>
        </div>
        {resolveAssetFileId(section.assets?.sideImage) ? (
          <div className={`hidden md:block transition-all duration-1000 delay-150 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <img
              src={getImageUrl(resolveAssetFileId(section.assets.sideImage))}
              alt={section.content?.title || 'lead-form'}
              className="h-full w-full rounded-[32px] object-cover shadow-xl transition duration-700 hover:scale-[1.02]"
            />
          </div>
        ) : null}
        <SectionVideoEmbed
          url={section.content?.youtubeUrl}
          title={section.content?.title || 'lead-form-video'}
          isVisible={isVisible}
          delayMs={220}
          badge="Recruit Video"
          className={`${resolveAssetFileId(section.assets?.sideImage) ? 'md:col-span-2' : ''} mt-2 max-w-5xl`}
        />
      </div>
    </section>
  )
}

export default function OrgLandingPageViewer({ orgId, landingPageData, orgName: providedOrgName, isPreview = false, editorFocusSectionId = '' }) {
  const [loading, setLoading] = React.useState(Boolean(orgId && !landingPageData))
  const [error, setError] = React.useState(null)
  const [landingPage, setLandingPage] = React.useState(landingPageData || null)
  const [orgName, setOrgName] = React.useState(providedOrgName || '')
  const [isScrolled, setIsScrolled] = React.useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const [consentPayload, setConsentPayload] = React.useState(null)
  const sectionRefs = React.useRef({})

  React.useEffect(() => {
    if (landingPageData) {
      setLandingPage(landingPageData)
      setError(null)
      setLoading(false)
      return
    }
    if (!orgId) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await api.get(`/orgs/${orgId}/landing-page/public`)
        const data = res?.data?.data || res?.data
        if (!cancelled) {
          setLandingPage(data || null)
        }
        try {
          const orgRes = await api.get(`/orgs/${orgId}`)
          const orgData = orgRes?.data?.data || orgRes?.data
          if (!cancelled) setOrgName(orgData?.name || '')
        } catch {
          // ignore
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.status === 404 ? '공개된 홈페이지를 찾을 수 없습니다.' : '홈페이지를 불러오지 못했습니다')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [orgId, landingPageData])

  React.useEffect(() => {
    const target = isPreview ? document.querySelector('.preview-container') : window
    const onScroll = () => {
      const nextValue = isPreview
        ? (document.querySelector('.preview-container')?.scrollTop || 0) > 20
        : (window.scrollY || 0) > 20
      setIsScrolled(nextValue)
    }
    target?.addEventListener?.('scroll', onScroll)
    return () => target?.removeEventListener?.('scroll', onScroll)
  }, [isPreview])

  React.useEffect(() => {
    if (isPreview || !orgId) return
    const normalized = normalizeLandingPageData(landingPage || {})
    const hasLeadForm = normalized.sections.some((section) => section.type === 'lead-form' && section.enabled !== false)
    if (!hasLeadForm) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get(`/orgs/${orgId}/landing-page/public/consents`, { __silent: true })
        const data = res?.data?.data || res?.data
        if (!cancelled) setConsentPayload(data || null)
      } catch {
        if (!cancelled) setConsentPayload(null)
      }
    })()
    return () => { cancelled = true }
  }, [isPreview, orgId, landingPage])

  const normalized = normalizeLandingPageData(landingPage || {})
  const sections = normalized.sections.filter((section) => section?.enabled !== false)
  const navItems = buildNavigationFromSections(sections)
  const primaryColor = normalized.style?.primaryColor || '#0f172a'

  const scrollToSection = (sectionRef) => {
    const directTarget = sectionRefs.current[sectionRef]
    const matchedSection = sections.find((section) => section.id === sectionRef || section.type === sectionRef)
    const target = directTarget || (matchedSection ? sectionRefs.current[matchedSection.id] : null)
    if (target?.scrollIntoView) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setMobileMenuOpen(false)
    }
  }

  React.useEffect(() => {
    if (!isPreview || !editorFocusSectionId) return undefined
    let raf = 0
    let timer = 0
    raf = window.requestAnimationFrame(() => {
      timer = window.setTimeout(() => {
        const node = sectionRefs.current[editorFocusSectionId]
        node?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
      }, 40)
    })
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearTimeout(timer)
    }
  }, [editorFocusSectionId, isPreview, landingPage])

  if (loading) return <Loading />
  if (error) return <InlineError message={error} />
  if (!landingPage && !landingPageData) return null

  const navClassName = isPreview
    ? 'sticky top-0 z-10'
    : 'fixed left-0 right-0 top-0 z-50'

  return (
    <div className={`${isPreview ? 'preview-container relative isolate overflow-hidden' : 'min-h-screen'} bg-white`}>
      <style>{`
        .org-hero-zoom {
          animation: orgHeroZoom 18s ease-in-out infinite alternate;
        }
        .org-float {
          animation: orgFloat 9s ease-in-out infinite;
        }
        .org-soft-pulse {
          animation: orgSoftPulse 3.8s ease-in-out infinite;
        }
        @keyframes orgHeroZoom {
          from { transform: scale(1); }
          to { transform: scale(1.08); }
        }
        @keyframes orgFloat {
          0%, 100% { transform: translate3d(0, 0, 0); }
          50% { transform: translate3d(0, -18px, 0); }
        }
        @keyframes orgSoftPulse {
          0%, 100% { opacity: 0.82; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.03); }
        }
      `}</style>
      <nav
        className={`${navClassName} transition-all duration-300`}
        style={{
          backgroundColor: isScrolled ? 'rgba(255,255,255,0.96)' : 'transparent',
          backdropFilter: isScrolled ? 'blur(16px)' : 'none',
          boxShadow: isScrolled ? '0 12px 32px rgba(15, 23, 42, 0.08)' : 'none',
        }}
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6 md:h-20">
          <button type="button" onClick={() => scrollToSection('hero')} className="flex items-center gap-3">
            {normalized.logoFileId ? (
              <img src={getImageUrl(normalized.logoFileId)} alt={orgName || '로고'} className="h-10 object-contain" />
            ) : (
              <span className={`text-lg font-black ${isScrolled ? 'text-slate-900' : 'text-white'}`}>{orgName || '조직 홈페이지'}</span>
            )}
          </button>
          <div className="hidden items-center gap-2 md:flex">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollToSection(item.sectionRef)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition ${isScrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white/90 hover:bg-white/10'}`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className={`md:hidden ${isScrolled ? 'text-slate-900' : 'text-white'}`}
          >
            메뉴
          </button>
        </div>
        {mobileMenuOpen ? (
          <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden">
            <div className="space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollToSection(item.sectionRef)}
                  className="block w-full rounded-xl px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </nav>

      <main>
        {sections.map((section) => {
          const commonProps = {
            key: section.id,
            sectionRef: (node) => { sectionRefs.current[section.id] = node },
            id: section.id,
          }
          return (
            <AnimatedSection {...commonProps}>
              {(visible) => (
                <>
                  {section.type === 'hero' ? <HeroSection section={section} primaryColor={primaryColor} onNavigate={scrollToSection} isVisible={visible} /> : null}
                  {section.type === 'ga-intro' || section.type === 'ceo-message' || section.type === 'business-intro'
                    ? <IntroSection section={section} primaryColor={primaryColor} isVisible={visible} />
                    : null}
                  {section.type === 'stats' ? <StatsSection section={section} primaryColor={primaryColor} isVisible={visible} /> : null}
                  {section.type === 'benefits' ? <BenefitsSection section={section} isVisible={visible} /> : null}
                  {section.type === 'lead-form'
                    ? <LeadFormSection section={section} orgId={orgId} isPreview={isPreview} primaryColor={primaryColor} consentPayload={consentPayload} isVisible={visible} />
                    : null}
                  {section.type === 'contact' ? <ContactSection section={section} primaryColor={primaryColor} isVisible={visible} /> : null}
                </>
              )}
            </AnimatedSection>
          )
        })}
      </main>

      <footer className="bg-slate-950 px-6 py-8 text-center text-sm text-white/55">
        {(orgName || providedOrgName) ? `© ${new Date().getFullYear()} ${orgName || providedOrgName}. All rights reserved.` : ''}
      </footer>
    </div>
  )
}
